// columns/TargetValueEditCellRenderer.tsx
import React, { useRef, useCallback } from "react";
import { TextareaAutosize } from "@mui/material";
import { Label } from "../../../model/Label";
import { useTranslationCellKeyboard } from "../../../hooks/useTranslationCellKeyboard";

interface TargetValueEditCellRendererProps {
  params: any;
  onSave: (label: Label, forceMode?: boolean) => Promise<void>;
}

const TargetValueEditCellRenderer: React.FC<TargetValueEditCellRendererProps> = ({
  params,
  onSave,
}) => {
  const hasError = params.row.hasError;
  const originalValue = useRef(params.value);

  const saveChanges = useCallback((forceMode = false) => {
    if (onSave && params.value !== originalValue.current) {
      const updatedLabel: Label = {
        ...params.row,
        targetValue: params.value,
        translated: !!params.value && params.value.trim().length > 0,
        hasError: false,
        errorMessage: undefined,
      };
      onSave(updatedLabel, forceMode);
    }
  }, [onSave, params]);

  const { handleKeyDown } = useTranslationCellKeyboard({ params, onSave: saveChanges });

  const handleBlur = useCallback(() => {
    params.api.stopCellEditMode({ id: params.id, field: params.field });
    saveChanges(false);
  }, [params, saveChanges]);

  const textareaClassName = hasError 
    ? "target-edit-textarea w-full p-2 border-2 rounded focus:outline-none focus:ring-2 resize-none border-red-400 focus:ring-red-300 bg-red-50"
    : "target-edit-textarea w-full p-2 border-2 rounded focus:outline-none focus:ring-2 resize-none border-blue-400 focus:ring-blue-300 bg-white";

  return (
    <TextareaAutosize
      value={params.value || ""}
      onChange={(e) => params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value })}
      onKeyDown={handleKeyDown}
      className={textareaClassName}
      autoFocus
      placeholder="Enter translation here..."
      onBlur={handleBlur}
      minRows={4}
    />
  );
};

export default TargetValueEditCellRenderer;